import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Loader2, Lock, Download, Search, Image, Mic, FileIcon } from "lucide-react";
import ChatMessage from "@/components/ChatMessage";
import FilterBar from "@/components/FilterBar";
import Timeline from "@/components/Timeline";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { parseWhatsAppZip, WhatsAppMessage } from "@/utils/whatsappParser";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DateRange } from "react-day-picker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";

const SharedChat = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [expired, setExpired] = useState(false);

  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [stats, setStats] = useState({
    totalMessages: 0,
    imageCount: 0,
    audioCount: 0,
    documentCount: 0,
  });
  const [chatFileName, setChatFileName] = useState<string>("");
  const [aiSummary, setAiSummary] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [chatId, setChatId] = useState<string>("");
  const [adding, setAdding] = useState(false);

  const filteredMessages = useMemo(() => {
    let filtered = [...messages];

    if (activeFilter) {
      filtered = filtered.filter(msg => {
        if (!msg.attachment) return false;
        if (activeFilter === 'images') return msg.attachment.type === 'image';
        if (activeFilter === 'audio') return msg.attachment.type === 'audio';
        if (activeFilter === 'documents') return msg.attachment.type === 'document';
        return false;
      });
    }

    if (dateRange?.from) {
      const fromTime = dateRange.from.getTime();
      const toTime = dateRange.to ? dateRange.to.getTime() : fromTime + 86400000;
      filtered = filtered.filter(msg => {
        const msgTime = msg.timestamp.getTime();
        return msgTime >= fromTime && msgTime <= toTime;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg => {
        return msg.content.toLowerCase().includes(query) ||
               msg.sender.toLowerCase().includes(query) ||
               msg.attachment?.aiDescription?.toLowerCase().includes(query) ||
               msg.attachment?.transcription?.toLowerCase().includes(query);
      });
    }

    return filtered;
  }, [messages, activeFilter, dateRange, searchQuery]);

  useEffect(() => {
    loadSharedChat();
  }, [shareId]);

  const loadSharedChat = async () => {
    if (!shareId) return;

    try {
      const { data, error } = await supabase.functions.invoke('get-shared-chat', {
        body: { token: shareId, passwordHash: null },
      });

      if (error || !data) {
        toast({
          title: t("share.notFound"),
          description: t("share.invalidLink"),
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      const result = data as any;
      if (result.status === 'not_found') {
        toast({ title: t("share.notFound"), description: t("share.invalidLink"), variant: "destructive" });
        navigate("/");
        return;
      }
      if (result.status === 'expired') {
        setExpired(true);
        setLoading(false);
        return;
      }
      if (result.status === 'password_required') {
        setPasswordRequired(true);
        setLoading(false);
        return;
      }
      if (result.status === 'ok' && result.chat) {
        await loadChatData(result.chat);
      }
    } catch (error) {
      console.error('Error loading shared chat:', error);
      toast({
        title: t("share.loadError"),
        description: t("share.failedToLoad"),
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const verifyPassword = async () => {
    setVerifying(true);
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { data: result, error } = await supabase.functions.invoke('get-shared-chat', {
        body: { token: shareId, passwordHash },
      });

      const payload = result as any;
      if (error || !payload || payload.status !== 'ok') {
        toast({
          title: t("share.incorrectPassword"),
          description: t("share.tryAgain"),
          variant: "destructive",
        });
        setVerifying(false);
        return;
      }

      setPasswordRequired(false);
      await loadChatData(payload.chat);
    } catch (error) {
      console.error('Error verifying password:', error);
      toast({
        title: t("share.verificationError"),
        variant: "destructive",
      });
      setVerifying(false);
    }
  };

  const loadChatData = async (chat: any) => {
    try {
      setChatId(chat.id);
      setChatFileName(chat.file_name);
      setAiSummary(chat.ai_summary || '');

      // Load messages from database (already parsed during upload)
      if (chat.messages_data) {
        const parsedMessages = chat.messages_data as any[];
        
        // Load existing AI data
        const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
          ? chat.attachment_ai_data as Record<string, any>
          : {};

        // Merge AI data with parsed messages
        const messagesWithAi = parsedMessages.map(msg => {
          if (msg.attachment && existingAiData[msg.id]) {
            const aiData = existingAiData[msg.id] as any;
            return {
              ...msg,
              attachment: {
                ...msg.attachment,
                aiDescription: aiData.aiDescription,
                transcription: aiData.transcription
              }
            };
          }
          return msg;
        });

        setMessages(messagesWithAi);
        
        // Calculate stats from messages
        const stats = {
          totalMessages: parsedMessages.length,
          imageCount: parsedMessages.filter(m => m.attachment?.type === 'image').length,
          audioCount: parsedMessages.filter(m => m.attachment?.type === 'audio').length,
          documentCount: parsedMessages.filter(m => m.attachment?.type === 'document').length,
        };
        setStats(stats);
        setLoading(false);
      } else {
        // Legacy support: Download and parse ZIP file if messages_data doesn't exist
        if (!chat.storage_path) {
          throw new Error('No chat data available');
        }

        const { data: fileBlob, error: downloadError } = await supabase.storage
          .from('chat-exports')
          .download(chat.storage_path);

        if (downloadError || !fileBlob) {
          throw new Error('Failed to download file');
        }

        const file = new File([fileBlob], chat.file_name, { type: 'application/zip' });
        const parsed = await parseWhatsAppZip(file, () => {});

        // Load AI data
        const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
          ? chat.attachment_ai_data as Record<string, any>
          : {};

        const messagesWithAi = parsed.messages.map(msg => {
          if (msg.attachment && existingAiData[msg.id]) {
            const aiData = existingAiData[msg.id] as any;
            return {
              ...msg,
              attachment: {
                ...msg.attachment,
                aiDescription: aiData.aiDescription,
                transcription: aiData.transcription
              }
            };
          }
          return msg;
        });

        setMessages(messagesWithAi);
        setStats(parsed.stats);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading chat data:', error);
      toast({
        title: t("share.loadError"),
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleAddToHistory = async () => {
    if (!user || !chatId) {
      navigate("/auth");
      return;
    }

    setAdding(true);
    try {
      // Get the original chat
      const { data: originalChat } = await supabase
        .from('chat_analyses')
        .select('*')
        .eq('id', chatId)
        .single();

      if (!originalChat) throw new Error('Chat not found');

      // Copy the chat to user's account
      const { error } = await supabase
        .from('chat_analyses')
        .insert({
          user_id: user.id,
          file_name: originalChat.file_name,
          storage_path: originalChat.storage_path,
          html_content: originalChat.html_content,
          language: originalChat.language,
          whisper_mode: originalChat.whisper_mode,
          context: originalChat.context,
          ai_summary: originalChat.ai_summary,
          short_summary: originalChat.short_summary,
          attachment_ai_data: originalChat.attachment_ai_data,
          messages_data: originalChat.messages_data,
          message_count: originalChat.message_count,
          image_count: originalChat.image_count,
          audio_count: originalChat.audio_count,
          document_count: originalChat.document_count,
          processing_status: 'completed',
        });

      if (error) throw error;

      toast({
        title: t("share.addedToHistory"),
        description: t("share.chatCopied"),
      });

      navigate("/history");
    } catch (error) {
      console.error('Error adding to history:', error);
      toast({
        title: t("share.addError"),
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-4">
          <div className="text-destructive">
            <Lock className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold">{t("share.expired")}</h2>
            <p className="text-muted-foreground mt-2">{t("share.expiredDesc")}</p>
          </div>
          <Button onClick={() => navigate("/")} className="w-full">
            {t("share.goHome")}
          </Button>
        </Card>
      </div>
    );
  }

  if (passwordRequired) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 space-y-4">
          <div className="text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-bold">{t("share.passwordRequired")}</h2>
            <p className="text-muted-foreground mt-2">{t("share.enterPasswordDesc")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("share.password")}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("share.enterPassword")}
              onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
            />
          </div>
          <Button onClick={verifyPassword} disabled={verifying || !password} className="w-full">
            {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("share.unlock")}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b shadow-soft sticky top-0 z-10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl sm:text-2xl font-bold text-primary">
                {t("share.sharedChat")}
              </h1>
              {chatFileName && (
                <p className="text-sm text-muted-foreground truncate">
                  {chatFileName}
                </p>
              )}
            </div>
            {user && (
              <Button onClick={handleAddToHistory} disabled={adding}>
                {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Download className="mr-2 h-4 w-4" />
                {t("share.addToMyChats")}
              </Button>
            )}
            {!user && (
              <Button onClick={() => navigate("/auth")} variant="outline">
                {t("share.signInToAdd")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <FilterBar 
        activeFilter={activeFilter} 
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExportPDF={() => {}}
        hideExport
      />

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl sm:rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold">{t("viewer.messages")}</h2>
                {(activeFilter || dateRange?.from || searchQuery) && (
                  <p className="text-sm text-muted-foreground">
                    {t("viewer.showingMessages")
                      .replace("{filtered}", filteredMessages.length.toString())
                      .replace("{total}", messages.length.toString())}
                  </p>
                )}
              </div>
              
              <div className="space-y-3 sm:space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredMessages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    onRerunAI={() => {}}
                    isProcessing={false}
                    disableRerun={true}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4">
              <Card className="p-3 sm:p-4 bg-gradient-primary text-primary-foreground">
                <div className="flex items-center gap-2 sm:gap-3">
                  <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
                  <div>
                    <p className="text-xs sm:text-sm opacity-90">{t("viewer.totalMessages")}</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.totalMessages}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Image className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.images")}</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.imageCount}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Mic className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.audio")}</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.audioCount}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <FileIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.documents")}</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.documentCount}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div className="space-y-4">
            <Timeline messages={filteredMessages} />
            
            {aiSummary && (
              <div className="bg-card rounded-xl shadow-soft p-4">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {t("viewer.aiSummary")}
                </h3>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{aiSummary}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedChat;
